import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";
import { useLines } from "@/features/lines/hooks";
import { CharacteristicFilter } from "./CharacteristicFilter";
import { PeriodPicker } from "./PeriodPicker";
import { ScopePicker } from "./ScopePicker";
import { StageBlock } from "./StageBlock";
import { useProductionReport } from "./hooks";
import { downloadReportCsv } from "./lib/report-export";
import { useReportState } from "./useReportState";

// Spec 083 (T039) — o CONTROLE DE PRODUÇÃO.
//
// Orquestrador puro: estado na URL, uma leitura, composição. Tudo que desenha mora ao lado.
//
// ⚠️ A ORDEM DOS ESTADOS não é estilo. Falha de leitura é declarada ANTES de decidir entre vazio e
// cheio: tratar erro como "não há dados" transformaria uma API fora do ar em "a fábrica não
// produziu nada", que é a mentira mais cara que esta tela poderia contar.

interface Props {
  /** Quando presente, a tela é a aba do detalhe: recorte travado e sem seletor de linha. */
  lockedLineId?: string;
}

export function ProductionReportView({ lockedLineId }: Props) {
  const state = useReportState(lockedLineId);
  const { data: lines = [], isLoading: linesLoading } = useLines();
  const lineId = state.lineId || lines[0]?.id || "";

  const query = useProductionReport(
    lineId
      ? {
          scope: state.scope,
          lineId,
          from: state.from,
          to: state.to,
          groupBy: state.groupBy,
          characteristicId: state.characteristicId,
          value: state.value,
          compareBy: state.compareBy,
        }
      : null
  );

  // ⚠️ `!linesLoading` no predicado: enquanto a lista carrega ela é `[]`, e sem isso a tela afirmava
  // "nenhuma linha cadastrada" por um instante em TODA visita — a fábrica inteira sumindo e voltando.
  const semLinha = !lineId && !linesLoading && lines.length === 0;
  const vazio =
    query.data?.stages.every((s) => s.products.length === 0 && s.input.quantity === null) ?? false;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-wrap items-end gap-4">
          {!lockedLineId ? (
            <ScopePicker
              scope={state.scope}
              lineId={lineId}
              onChange={(next) =>
                state.set({
                  scope: next.scope === "chain" ? "chain" : null,
                  // `lineId` é o mesmo nome que o assistente monta; `line` sai da URL para não
                  // sobrar um segundo nome para a mesma coisa.
                  lineId: next.lineId,
                  line: null,
                })
              }
            />
          ) : null}
          <PeriodPicker
            preset={state.preset}
            onPresetChange={(p) => state.set({ preset: p })}
            from={state.from}
            to={state.to}
            onRangeChange={(r) => state.set({ preset: "custom", from: r.from, to: r.to })}
            groupBy={state.groupBy}
            onGroupByChange={(g) => state.set({ groupBy: g })}
          />
          <CharacteristicFilter
            characteristicId={state.characteristicId}
            value={state.value}
            compareBy={state.compareBy}
            filter={query.data?.filter ?? null}
            onChange={(patch) => state.set(patch)}
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!query.data}
          onClick={() => query.data && downloadReportCsv(query.data, state.mode, state.indicatorId)}
        >
          <Download className="mr-1 h-4 w-4" aria-hidden="true" /> Exportar
        </Button>
      </div>

      {query.isError ? (
        <Notify.QueryError
          error={query.error}
          onRetry={() => void query.refetch()}
          title="Não foi possível carregar o controle de produção"
        />
      ) : semLinha ? (
        <Notify.Empty
          title="Nenhuma linha de produção cadastrada"
          description="O controle de produção lê o que as linhas registram. Cadastre uma linha para começar."
        />
      ) : !lineId || query.isLoading ? (
        <p className="text-[13px] text-text-secondary">Carregando o período…</p>
      ) : vazio ? (
        <Notify.Empty
          title="Nenhum registro no período"
          description="Troque o período, ou registre produção na linha para que ela apareça aqui."
        />
      ) : (
        query.data?.stages.map((stage) => (
          <StageBlock
            key={stage.lineId}
            stage={stage}
            // Uma etapa só não é cadeia: "Etapa 1" sozinha no cabeçalho não diz nada que o nome
            // da linha já não diga.
            stageNumber={(query.data?.stages.length ?? 0) > 1 ? stage.level + 1 : null}
            groupBy={state.groupBy}
            mode={state.mode}
            onModeChange={(m) => state.set({ grid: m === "full" ? "full" : null })}
            heroId={state.indicatorId}
            onHeroChange={(id) => state.set({ indicator: id })}
            showLineLink={!lockedLineId}
            comparison={query.data?.comparison ?? null}
            filter={query.data?.filter ?? null}
          />
        ))
      )}
    </div>
  );
}
